import React, { useContext } from 'react';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay';
import tracking from 'dibs-tracking';
import { trackSbViewMoreCtaClick } from 'dibs-recent-history/src/recentHistoryTracking';
import { getAbTestTrackingData } from 'dibs-buyer-layout/exports/clientABTestHelpers';

// components
import { SbSharedFollowSearchProvider } from '../SbSharedFollowSearchProvider/SbSharedFollowSearchProvider';
import { SbRespFollowSearchTopButton } from '../SbRespFollowSearchTopButton/SbRespFollowSearchTopButton';
import { SbRespFollowSearchBannerLazy } from '../SbRespFollowSearchBanner/SbRespFollowSearchBannerLazy';
import { SbSharedTitle } from '../SbSharedTitle/SbSharedTitle';
import { SbSharedPageBreadcrumbs } from '../SbSharedPageBreadcrumbs/SbSharedPageBreadcrumbs';
import { SbSharedTopTileController } from '../SbSharedTopTileController/SbSharedTopTileController';
import { SbRespCategoryTiles } from '../SbRespCategoryTiles/SbRespCategoryTiles';
import { SbRespDesignTiles } from '../SbRespDesignTiles/SbRespDesignTiles';
import { SbSharedBillboardAd } from '../SbSharedBillboardAd/SbSharedBillboardAd';
import { SbRespNullSearchResults } from '../SbRespNullSearchResults/SbRespNullSearchResults';
import { SbSharedWordpressArticles } from '../SbSharedWordpressArticles/SbSharedWordpressArticles';

import { SharedRespMoreWaysToBrowse } from '../../shared/SharedRespMoreWaysToBrowse/SharedRespMoreWaysToBrowse';
import { SharedRelatedPages } from '../../shared/SharedRelatedPages/SharedRelatedPages';
import { SharedRespLinksBlock } from '../../shared/SharedRespLinksBlock/SharedRespLinksBlock';
import { SbRespNotificationUnavailableItem } from '../SbRespNotifications/SbRespNotificationUnavailableItem/SbRespNotificationUnavailableItem';
import { SbRespPriceTiles } from '../SbRespPriceTiles/SbRespPriceTiles';
import { SharedRecentlyViewedItems } from '../../shared/SharedRecentlyViewedItems/SharedRecentlyViewedItems';
import { TradePromoLoader } from '../../global/TradePromo/TradePromoLoader';
import { SbRespTradeOnApprovalLoadingBanner } from '../SbRespTradeOnApprovalPromo/SbRespTradeOnApprovalPromoBanner/SbRespTradeOnApprovalLoadingBanner';
import { SbSharedPersonalizedFilterToast } from '../SbSharedPersonalizedFilterToast/SbSharedPersonalizedFilterToast';
import { SbRespMainContent } from '../SbRespMainContent/SbRespMainContent';
import { SbSharedForceSearchLink } from '../SbSharedForceSearchLink/SbSharedForceSearchLink';
import { SbSharedRedirectedItem } from '../SbSharedRedirectedItem/SbSharedRedirectedItem';
import { SbRespContentModuleSelector } from '../SbRespContentModuleSelector/SbRespContentModuleSelector';
import { FormattedMessage } from 'react-intl';
import { SbSharedControlledHeadingWrapper } from '../SbSharedControlledHeadingWrapper/SbSharedControlledHeadingWrapper';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { hasAppliedCategories } from '../../utils/categoryHelpers';
import { trackRerankEngagementScoreTest } from '../../utils/tracking/searchBrowse/cdnTestTracking';

// constants
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';
import { FILTER_REFINE_SEARCH } from '../SbSharedRefineMenu/sbSharedRefineMenuConstants';
import { MIN_THIN_PAGE_COUNT } from '../../constants/sbConstants';

const { SEARCH, EXCLUSIVE } = pageTypes;

// styles
import styles from './SbRespComponents.scss';

const SbRespTradeOnApprovalPromoBannerLazy = React.lazy(() =>
    import(
        /* webpackChunkName: "TradeOnApprovalPromo" */ '../SbRespTradeOnApprovalPromo/SbRespTradeOnApprovalPromoBanner/SbRespTradeOnApprovalPromoBannerLazy'
    )
);

function getSearchTerm(filters, isSearchPage) {
    const searchKey = isSearchPage ? SEARCH : FILTER_REFINE_SEARCH;

    return get(
        filters.filter(({ name }) => name === searchKey),
        '[0].values[0].urlLabel'
    );
}

function SbRespComponentsComponent(props) {
    const {
        viewer,
        currency,
        itemSearch,
        user,
        page,
        generatedUriRef,
        isClient,
        relay,
        relayVariables,
        shippingFilters,
    } = props;

    const { isSbTopNavVariant } = useContext(ServerVarsContext);

    const {
        totalResults,
        appliedFilters,
        pageType,
        internalBuyLinks = [],
        canBeLocationSearch,
        displayUriRef,
    } = itemSearch;

    const {
        isBillboardAdEnabled,
        isMoreWaysToBrowseEnabled,
        isThinItemSearchEnabled,
        isFollowSearchEnabled,
        isContentModuleEnabled,
        isWordpressArticlesEnabled,
    } = viewer;

    React.useEffect(() => {
        if (canBeLocationSearch) {
            const trackingData = getAbTestTrackingData(document.cookie, 'SEARCH_LOCATION_TEST');
            tracking.trackAbTestEvent(trackingData, 'SEARCH_BROWSE_DESKTOP');
        }
    }, [canBeLocationSearch]);

    const isSearch = pageType === SEARCH;
    const showNullSearchResultPage = !totalResults;
    const isThinPage = totalResults < MIN_THIN_PAGE_COUNT && isThinItemSearchEnabled;
    const isThinBrowsePage = !isSearch && isThinPage;
    const searchTerm = getSearchTerm(appliedFilters, isSearch);
    const hasSpellCorrectSearch = get(itemSearch, 'searchCorrections.originalSearchTerm');
    const isExclusivePage = pageType === EXCLUSIVE;
    const hasCategories = hasAppliedCategories(appliedFilters);

    React.useEffect(() => {
        trackRerankEngagementScoreTest({
            isSearch,
            hasCategories,
            pageContext: 'SEARCH_RESPONSIVE',
        });
        // displayUriRef is needed to refire tracking after applied filters were updated.
    }, [isSearch, hasCategories, displayUriRef]);

    const spellCorrectSearchHeader = hasSpellCorrectSearch ? (
        <FormattedMessage
            id="sb.SbRespComponents.spellTitle"
            defaultMessage="Showing results for"
        />
    ) : (
        undefined
    );

    if (showNullSearchResultPage) {
        return (
            <SbRespNullSearchResults
                user={user}
                page={page}
                viewer={viewer}
                searchTerm={searchTerm}
                uriRef={generatedUriRef}
                itemSearch={itemSearch}
                isClient={isClient}
            />
        );
    }

    return (
        <SbSharedFollowSearchProvider
            user={user}
            itemSearch={itemSearch}
            uriRef={generatedUriRef}
            page={page}
            searchTerm={searchTerm}
            thinTrackingEnabled={isThinPage}
        >
            {({
                showFollowButton,
                isFollowing,
                followSearchValue,
                refetchFollow,
                isCategorySearch,
            }) => (
                <React.Fragment>
                    {/* Breadcrumbs */}
                    {!isSearch && !isExclusivePage && (
                        <SbSharedPageBreadcrumbs itemSearch={itemSearch} />
                    )}

                    {/* Redirected item */}
                    <SbSharedRedirectedItem />

                    <SbSharedControlledHeadingWrapper itemSearch={itemSearch}>
                        {Heading => (
                            <>
                                {/* Page Header */}
                                <div className={styles.headerContainer}>
                                    <div className={styles.headerLeftWrapper}>
                                        <div className={styles.headerLeft}>
                                            <SbSharedTitle
                                                itemSearch={itemSearch}
                                                searchTerm={searchTerm}
                                                customSearchHeader={spellCorrectSearchHeader}
                                                Heading={Heading}
                                                showFollowButton={
                                                    isFollowSearchEnabled && showFollowButton
                                                }
                                                headerWrapperClass={
                                                    isSbTopNavVariant
                                                        ? styles.titleHeaderWrapper
                                                        : undefined
                                                }
                                            >
                                                <SbRespFollowSearchTopButton
                                                    user={user}
                                                    uriRef={generatedUriRef}
                                                    showFollowButton={showFollowButton}
                                                    followSearchValue={followSearchValue}
                                                    isFollowing={isFollowing}
                                                    refetchFollow={refetchFollow}
                                                    isCategorySearch={isCategorySearch}
                                                />
                                            </SbSharedTitle>
                                        </div>
                                    </div>
                                    {relayVariables.isTrade && isClient && (
                                        <div className={styles.tradePromoMessage}>
                                            <TradePromoLoader
                                                showIcon
                                                user={user}
                                                displayInfo={null}
                                                item={null}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Content Module */}
                                {isContentModuleEnabled && (
                                    <SbRespContentModuleSelector itemSearch={itemSearch} />
                                )}

                                <SbSharedForceSearchLink
                                    itemSearch={itemSearch}
                                    searchTerm={searchTerm}
                                />

                                {/* Broad Search Category Tiles, or, for trade users, Price Range Tiles or On-Approval banner)*/}
                                <SbSharedTopTileController
                                    itemSearch={itemSearch}
                                    categoryTiles={<SbRespCategoryTiles itemSearch={itemSearch} />}
                                    designTiles={<SbRespDesignTiles itemSearch={itemSearch} />}
                                    tradeOnApprovalBanner={
                                        /* FINDING-13641 Workaround until React Suspense supports server-side
                                         * rendering > https://reactjs.org/docs/code-splitting.html#reactlazy
                                         */
                                        isClient ? (
                                            <React.Suspense
                                                fallback={<SbRespTradeOnApprovalLoadingBanner />}
                                            >
                                                <SbRespTradeOnApprovalPromoBannerLazy
                                                    itemSearch={itemSearch}
                                                    viewer={viewer}
                                                />
                                            </React.Suspense>
                                        ) : (
                                            <SbRespTradeOnApprovalLoadingBanner />
                                        )
                                    }
                                    priceTiles={
                                        <SbRespPriceTiles itemSearch={itemSearch} viewer={viewer} />
                                    }
                                    viewer={viewer}
                                />
                                <SbRespMainContent
                                    currency={currency}
                                    itemSearch={itemSearch}
                                    viewer={viewer}
                                    user={user}
                                    isClient={isClient}
                                    isThinPage={isThinPage}
                                    isThinBrowsePage={isThinBrowsePage}
                                    relay={relay}
                                />

                                {/* Follow Search */}
                                {isClient && (
                                    <SbRespFollowSearchBannerLazy
                                        viewer={viewer}
                                        user={user}
                                        showFollowButton={showFollowButton}
                                        isFollowing={isFollowing}
                                        followSearchValue={followSearchValue}
                                        uriRef={generatedUriRef}
                                        buttonSize="large"
                                        refetchFollow={refetchFollow}
                                        isCategorySearch={isCategorySearch}
                                    />
                                )}
                                {/* Ad */}
                                {!isThinPage && isBillboardAdEnabled && (
                                    <SbSharedBillboardAd
                                        itemSearch={itemSearch}
                                        adWidth="desktop"
                                    />
                                )}
                                {/* Editorial */}
                                {isWordpressArticlesEnabled && (
                                    <SbSharedWordpressArticles itemSearch={itemSearch} />
                                )}
                                {/* Recently Viewed Items */}
                                {
                                    <SharedRecentlyViewedItems
                                        isClient={isClient}
                                        user={user}
                                        onViewMoreCtaClick={trackSbViewMoreCtaClick}
                                    />
                                }
                                {/*  Related Pages */}
                                <SharedRelatedPages
                                    itemSearch={itemSearch}
                                    LinksBlockComponent={SharedRespLinksBlock}
                                    dataTn={'sb-resp-related-pages'}
                                    noBottomMargin={
                                        isMoreWaysToBrowseEnabled &&
                                        internalBuyLinks &&
                                        internalBuyLinks.length > 0
                                    }
                                />
                                {/*  More Ways to Browse */}
                                {isMoreWaysToBrowseEnabled && (
                                    <SharedRespMoreWaysToBrowse
                                        internalBuyLinks={internalBuyLinks}
                                    />
                                )}
                            </>
                        )}
                    </SbSharedControlledHeadingWrapper>

                    {/* Unavailable Item Modal */}
                    <SbRespNotificationUnavailableItem />
                    {/* Personalized Filter Toast */}
                    <SbSharedPersonalizedFilterToast shippingFilters={shippingFilters} />
                </React.Fragment>
            )}
        </SbSharedFollowSearchProvider>
    );
}

SbRespComponentsComponent.propTypes = {
    itemSearch: PropTypes.object.isRequired,
    page: PropTypes.number,
    user: PropTypes.object,
    isClient: PropTypes.bool,
    viewer: PropTypes.object,
    generatedUriRef: PropTypes.string,
    relay: PropTypes.object.isRequired,
    currency: PropTypes.string.isRequired,
    internalBuyLinks: PropTypes.object,
    relayVariables: PropTypes.shape({
        isTrade: PropTypes.bool,
    }).isRequired,
    shippingFilters: PropTypes.array,
};

const mapStateToProps = state => {
    const { filters, relayVariables } = state;
    const { generatedUriRef, refetchInFlight, page } = filters;

    return {
        page,
        refetchInFlight,
        generatedUriRef,
        relayVariables: relayVariables.variables,
    };
};

export const SbRespComponents = createFragmentContainer(
    connect(mapStateToProps)(SbRespComponentsComponent),
    {
        itemSearch: graphql`
            fragment SbRespComponents_itemSearch on ItemSearchQueryConnection {
                totalResults
                pageType
                searchCorrections {
                    originalSearchTerm
                }
                canBeLocationSearch
                ...SbSharedControlledHeadingWrapper_itemSearch
                ...SbSharedForceSearchLink_itemSearch
                ...SbSharedSearchSuggestions_itemSearch
                ...SbRespNullSearchResults_itemSearch
                ...SbSharedPageBreadcrumbs_itemSearch
                ...SbSharedTitle_itemSearch
                ...SbSharedTopTileController_itemSearch
                ...SbRespCategoryTiles_itemSearch
                ...SbRespDesignTiles_itemSearch
                ...SbSharedFollowSearchProvider_itemSearch
                ...SbSharedBillboardAd_itemSearch
                ...SbRespPriceTiles_itemSearch @include(if: $isTrade)
                ...SbRespTradeOnApprovalPromoBanner_itemSearch @include(if: $isTrade)
                ...SbRespMainContent_itemSearch
                ...SbRespContentModuleSelector_itemSearch
                ...SbSharedWordpressArticles_itemSearch
                appliedFilters {
                    name
                    values {
                        urlLabel
                    }
                }
                ...SharedRelatedPages_itemSearch
                internalBuyLinks {
                    ...SharedRespMoreWaysToBrowse_internalBuyLinks
                }
                displayUriRef
            }
        `,
        user: graphql`
            fragment SbRespComponents_user on User {
                ...TradePromo_user @include(if: $isTrade)
                ...SbRespNullSearchResults_user
                ...SbRespFollowSearchBanner_user
                ...SbRespFollowSearchTopButton_user
                ...SbSharedFollowSearchProvider_user
                ...SharedRecentlyViewedItems_user
                ...SbRespMainContent_user
            }
        `,
        viewer: graphql`
            fragment SbRespComponents_viewer on Viewer {
                isBillboardAdEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isThinItemSearchEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isMoreWaysToBrowseEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isFollowSearchEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isContentModuleEnabled: isEnabledByLocale(featureName: "BROWSE_CONTENT_MODULES")
                isWordpressArticlesEnabled: featureFlag(feature: "wordpressArticles")
                ...SbRespNullSearchResults_viewer
                ...SbRespFollowSearchBanner_viewer
                ...SbRespPriceTiles_viewer @include(if: $isTrade)
                ...SbRespMainContent_viewer
                ...SbRespTradeOnApprovalPromoBanner_viewer @include(if: $isTrade)
                ...SbSharedTopTileController_viewer
            }
        `,
        shippingFilters: graphql`
            fragment SbRespComponents_shippingFilters on ShippingFilterType @relay(plural: true) {
                ...SbSharedPersonalizedFilterToast_shippingFilters
            }
        `,
    }
);
